import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import _ from 'lodash';

import { BodyText, Button, Div, Flex, GrowlNotification, Gutter, ModalDialog, Tag, Title } from '@gaiads/telia-react-component-library';
import {
  AssetsCard,
  AssetsCardTranslations,
  COUNTRIES,
  OffboardUser,
  OffboardUserTranslations,
  PageHeader,
  ProfileCard,
  ProfileCardTranslations,
  UserRightsAndLimitsCard,
  UserRightsAndLimitsCardTranslations,
} from '@cdaas/reusable-components-daas';
import {
  AdminUserAssetResponse,
  CompanyDevicePolicyStatus,
  InvoicingAccountOptionResponseContents,
  UpdateUserRequest,
  UserPoliciesResponseSingleDevicePolicyEnum,
} from '@cdaas/api-clients/dist/asset-admin-openapi';

import { AppDispatch } from '../../../store';
import { selectOrganization, setOrderOrganization, setOrderSalesChannel, setOrderUser } from '../../../store/order';
import { ASSET_MAX_LIMIT, clearSessionStorageOrderState, setStorageItems, UserResponseWithUserGroup } from '../../../utils';
import {
  assetAdminClient,
  assetAdminInvoicingAccountClient,
  assetAdminOrganizationClient,
  assetAdminUserClient,
  assetAdminUserGroupClient,
} from '../../../api-client/asset-admin-client';
import { productServiceAdminSalesChannelsClient } from '../../../api-client/product-service-client';

import { Loader } from '../../App/Loader';
import Message from '../../App/Message';
import { routes, withCustomerOrgFormPath } from '../../App/routes';
import { DetailsPageMainContent, DetailsPageMainContentLeftSide, DetailsPageMainContentRightSide } from '../../App/Layouts/DetailsPageLayout';

import InvoicingAccounts from '../../../Components/InvoicingAccounts/InvoicingAccounts';
import { selectFeatureFlags } from '../../../store/featureFlags';

interface LocationState {
  clickedFromPathname: string;
  clickedFromPage: string;
}

interface UserProfileParam {
  id: string;
  usergroupid: string;
  userid: string;
}

const UserProfile: FunctionComponent = () => {
  const organizationId = useParams<{ id: string }>().id;
  const params = useParams<UserProfileParam>();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const dispatch: AppDispatch = useDispatch();
  const [user, setUser] = useState<UserResponseWithUserGroup>({
    id: '',
    allowance: null,
    allowance_type: null,
    family_name: null,
    given_name: null,
    employee_id: null,
    invoicing_account: null,
    name: null,
    phone_number: '',
    phone_number_verified: false,
    email: '',
    user_roles: { effective: [], user: [], user_group: [] },
    allowed_procurements: [],
    policies: {
      effective_lease_procurement_limit: null,
      lease_procurement_limit: null,
      single_device_policy: UserPoliciesResponseSingleDevicePolicyEnum.Disabled,
      company_device_policy: CompanyDevicePolicyStatus.NotNeeded,
    },
    allow_access_without_bank_id: false,
    inherited_invoicing_account: {
      name: '',
    },
    party_id: null,
    prescheduled_onboarding_at: null,
    offboarding_at: null,
  });
  const [organizationInvoicingAccounts, setOrganizationInvoicingAccounts] = useState<InvoicingAccountOptionResponseContents[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [fetchUserError, setFetchUserError] = useState<boolean>(false);
  const [deleteUserError, setDeleteUserError] = useState<string | null>(null);
  const [initiateOrderOnBehalfError, setInitiateOrderOnBehalfError] = useState<boolean>(false);
  const [deleteUserModalIsOpen, setDeleteUserModalIsOpen] = useState<boolean>(false);
  const [assets, setAssets] = useState<AdminUserAssetResponse[]>([]);
  const [offboardUserModalIsOpen, setOffboardUserModalIsOpen] = useState<boolean>(false);

  const organization = useSelector(selectOrganization);
  const offboardingFeatureNoEnabled = useSelector(selectFeatureFlags).FEATURE_FLAG_USER_OFFBOARDING_ENABLED;
  const emailUpdateEnabled = useSelector(selectFeatureFlags).FEATURE_FLAG_EMAIL_UPDATE_ENABLED;

  const { t: tUser } = useTranslation('organizationForm');
  const { t: tGlobal } = useTranslation('global');

  const userRightsAndLimitsTranslations: UserRightsAndLimitsCardTranslations = {
    mainTitle: tUser('userPage.userRightsAndLimitsCard.mainTitle'),
    userRightsTitle: tUser('userPage.userRightsAndLimitsCard.userRightsTitle'),
    userRightsText: tUser('userPage.userRightsAndLimitsCard.userRightsText'),
    procurementLimitTitle: tUser('userPage.userRightsAndLimitsCard.procurementLimitTitle'),
    procurementLimitInputLabel: tUser('userPage.userRightsAndLimitsCard.procurementLimitInputLabel', {
      currency: organization?.locale.currency.toUpperCase(),
    }),
    procurementLimitOverrideCheckboxLabel: tUser('userPage.userRightsAndLimitsCard.procurementLimitOverrideCheckboxLabel'),
    procurementLimitRequiredError: tUser('userPage.userRightsAndLimitsCard.procurementLimitRequiredError'),
    procurementLimitNumberRequirdError: tUser('userPage.userRightsAndLimitsCard.procurementLimitNumberRequirdError'),
    saveButton: tUser('userPage.userRightsAndLimitsCard.saveButton'),
    cancelButton: tUser('userPage.userRightsAndLimitsCard.cancelButton'),
    adminRoleName: _.capitalize(tGlobal('userRoles.admin')),
    userRoleName: _.capitalize(tGlobal('userRoles.user')),
    userLiteRoleName: _.capitalize(tGlobal('userRoles.user-lite')),
    approverRoleName: _.capitalize(tGlobal('userRoles.approver')),
    ordererRoleName: _.capitalize(tGlobal('userRoles.orderer')),
    allowAccessWithoutBankIdTitle: tUser('userPage.userRightsAndLimitsCard.allowAccessWithoutBankIdTitle'),
    allowAccessWithoutBankIdText: tUser('userPage.userRightsAndLimitsCard.allowAccessWithoutBankIdText'),
    allowAccessWithoutBankIdLabel: tUser('userPage.userRightsAndLimitsCard.allowAccessWithoutBankIdLabel'),
    allowanceTitle: tUser('userPage.userRightsAndLimitsCard.allowanceTitle'),
    allowanceExplanation: tUser('userPage.userRightsAndLimitsCard.allowanceExplanation'),
    allowanceInputLabel: tUser('userPage.userRightsAndLimitsCard.allowanceInputLabel', { currency: organization?.locale.currency.toUpperCase() }),
    allowanceInputLabelOneTime: tUser('userPage.userRightsAndLimitsCard.allowanceInputLabelOneTime', {
      currency: organization?.locale.currency.toUpperCase(),
    }),
    allowanceUnlimited: tUser('userPage.userRightsAndLimitsCard.allowanceUnlimited'),
    allowanceEditLinkTo: tUser('userPage.userRightsAndLimitsCard.allowanceEditLinkTo'),
    minBedriftLinktext: tUser('userPage.userRightsAndLimitsCard.minBedriftLinktext'),
    errorTitle: tUser('userPage.userSavingFailedErrorTitle'),
    errorMessage: tUser('userPage.userSavingFailedErrorMessage'),
  };

  const profileCardTranslations: ProfileCardTranslations = {
    title: tUser('userPage.ProfileCard.title'),
    nameRowTitle: tUser('userPage.ProfileCard.name'),
    groupRowTitle: tUser('userPage.ProfileCard.employeeGroup'),
    emailRowTitle: tUser('userPage.ProfileCard.email'),
    inactiveUserName: tUser('userPage.ProfileCard.inactiveUser'),
    companyDevicePolicyTitle: tUser('userPage.ProfileCard.companyDevicePolicyTitle'),
    companyDevicePolicyIsAccepted: tUser('userPage.ProfileCard.companyDevicePolicyIsAccepted'),
    companyDevicePolicyIsNotAccepted: tUser('userPage.ProfileCard.companyDevicePolicyIsNotAccepted'),
    companyDevicePolicyNotApplicable: tUser('userPage.ProfileCard.companyDevicePolicyNotApplicable'),
    phoneNumberRowTitle: tUser('userPage.ProfileCard.phoneNumber'),
    employeeId: tUser('userPage.ProfileCard.employeeId'),
    preScheduledOnboardingTitle: tUser('userPage.ProfileCard.preScheduledOnboardingTitle'),
    preScheduledOnboardingDescription: tUser('userPage.ProfileCard.preScheduledOnboardingDescription'),
    offboardingTitle: tUser('userPage.ProfileCard.offboardingTitle'),
    offboardingLabel: tUser('userPage.ProfileCard.offboardingLabel'),
    offboardingDescription: tUser('userPage.ProfileCard.offboardingDescription'),
    offboardingDateInvalid: tUser('userPage.ProfileCard.offboardingDateInvalid'),
    offboardingDateNotSelected: tUser('userPage.ProfileCard.offboardingDateNotSelected'),
    offboardingCancel: tUser('userPage.ProfileCard.offboardingCancel'),
    offboardingCancelled: tUser('userPage.ProfileCard.offboardingCancelled'),
    phoneNumberConfirmationNoticeTitle: tUser('userPage.ProfileCard.phoneNumberVerificationNotificationTitle'),
    phoneNumberConfirmationNoticeMessage: tUser('userPage.ProfileCard.phoneNumberVerificationNotificationMessage'),
    disabledEmailText: tUser('userPage.ProfileCard.disabledEmailText'),
    validEmailNotification: tUser('userPage.ProfileCard.validEmailNotification'),
    validPhoneNumberNotification: tUser('userPage.ProfileCard.validPhoneNumberNotification'),
    validCountryCodeNotification: tUser('userPage.ProfileCard.validCountryCodeNotification'),
    phoneNumberRequiredNotification: tUser('userPage.ProfileCard.phoneNumberRequiredNotification'),
    countryCodeRequiredNotification: tUser('userPage.ProfileCard.countryCodeRequiredNotification'),
    saveButton: tUser('userPage.ProfileCard.saveButton'),
    cancelButton: tUser('userPage.ProfileCard.cancelButton'),
    saveErrorTitle: tUser('userPage.userSavingFailedErrorTitle'),
    saveErrorMessage: tUser('userPage.userSavingFailedErrorMessage'),
    countries: {
      Finland: tGlobal('countries.Finland'),
      Sweden: tGlobal('countries.Sweden'),
      Norway: tGlobal('countries.Norway'),
      Denmark: tGlobal('countries.Denmark'),
      UnitedKingdom: tGlobal('countries.UnitedKingdom'),
    },
    admin: {
      pimPartyIdTitle: tUser('userPage.ProfileCard.pimPartyIdTitle'),
    },
  };

  const assetsCardTranslations: AssetsCardTranslations = {
    title: tUser('userPage.assets.title'),
    noAssets: tUser('userPage.assets.noAssets'),
    endDate: tUser('userPage.assets.endDate'),
  };

  const offboardUserTranslations: OffboardUserTranslations = {
    title: tUser('userPage.offboardUserModal.title'),
    description: tUser('userPage.offboardUserModal.description'),
    name: tUser('userPage.offboardUserModal.name'),
    inactiveUser: tUser('userPage.offboardUserModal.inactiveUser'),
    phoneNumber: tUser('userPage.offboardUserModal.phoneNumber'),
    email: tUser('userPage.offboardUserModal.email'),
    devices: tUser('userPage.offboardUserModal.devices'),
    imei: tUser('userPage.offboardUserModal.imei'),
    endDate: tUser('userPage.offboardUserModal.endDate'),
    dateLabel: tUser('userPage.offboardUserModal.dateLabel'),
    invalidDate: tUser('userPage.offboardUserModal.invalidDate'),
    allowedActions: tUser('userPage.offboardUserModal.allowedActions'),
    blockedActions: tUser('userPage.offboardUserModal.blockedActions'),
    automaticActions: tUser('userPage.offboardUserModal.automaticActions'),
    additionalNote: tUser('userPage.offboardUserModal.additionalNote'),
    notificationTitle: tUser('userPage.offboardUserModal.notificationTitle'),
    notificationMessage: tUser('userPage.offboardUserModal.notificationMessage'),
    offboardButton: tUser('userPage.offboardUserModal.offboardButton'),
    cancelButton: tUser('userPage.offboardUserModal.cancelButton'),
    confirmationModal: {
      title: tUser('userPage.offboardUserModal.confirmationModal.title'),
      confirmInfo: tUser('userPage.offboardUserModal.confirmationModal.confirmInfo'),
      confirmButton: tUser('userPage.offboardUserModal.confirmationModal.confirmButton'),
      cancelButton: tUser('userPage.offboardUserModal.confirmationModal.cancelButton'),
    },
    errorMessage: tUser('userPage.offboardUserModal.errorMessage'),
  };

  useEffect(() => {
    clearSessionStorageOrderState();
  }, []);

  useEffect(() => {
    // Fetch all the backend data this component needs
    async function fetchData() {
      try {
        if (organization?.id !== organizationId) {
          const organizationFetched = (await assetAdminOrganizationClient.adminOrganizationRead(organizationId, 'user_groups,user_groups.users'))
            .data;
          dispatch(setOrderOrganization(organizationFetched));
        }
        const invoicingAccounts = await assetAdminInvoicingAccountClient.adminReadInvoicingAccountOptions(params.id, 99999);
        setOrganizationInvoicingAccounts(invoicingAccounts.data.contents);
      } catch {
        setOrganizationInvoicingAccounts([]);
      }
      // GETing invoicing accounts from asset service makes asset service update invoicing accounts information
      // (e.g. has some invoicing account been removed) from Telia finance.
      // GETing invoicing accounts before GETing user makes sure user's invoicing account has up-to-date info if it's active=true|false
      try {
        const resUser = await assetAdminUserClient.adminUserRead(params.id, params.usergroupid, params.userid);
        const resUserGroup = await assetAdminUserGroupClient.adminUserGroupRead(params.id, params.usergroupid);
        setUser({
          ...resUser.data,
          user_group: resUserGroup.data,
        });
        const userAssets = (await assetAdminClient.adminUserAssetsList(organizationId, resUserGroup.data.id, resUser.data.id, ASSET_MAX_LIMIT)).data
          .contents;
        setAssets(userAssets);
      } catch {
        setFetchUserError(true);
      } finally {
        setLoading(false);
      }
    }
    setLoading(true);

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id, params.usergroupid, params.userid, organization, organizationId]);

  const handleUserSave = async (userData: Partial<UpdateUserRequest>): Promise<boolean> => {
    const updateData: UpdateUserRequest = {
      phone_number: userData.phone_number || user.phone_number || '',
      email: userData.email || user.email,
      user_roles: userData.user_roles || user.user_roles.user,
      allowed_procurements: user.allowed_procurements,
      policies: {
        lease_procurement_limit: user.policies?.lease_procurement_limit,
      },
      employee_id: userData.employee_id ?? user.employee_id ?? undefined,
      allow_access_without_bank_id: userData.allow_access_without_bank_id ?? user.allow_access_without_bank_id,
      offboarding_at: 'offboarding_at' in userData ? userData.offboarding_at : (user.offboarding_at ?? undefined),
    };
    if (userData.policies?.lease_procurement_limit || userData.policies?.lease_procurement_limit === null) {
      updateData.policies!.lease_procurement_limit = userData.policies.lease_procurement_limit;
    }
    if (userData.invoicing_account !== null) {
      updateData.invoicing_account = userData.invoicing_account || user.invoicing_account?.id;
    } else {
      updateData.invoicing_account = null;
    }

    try {
      const res = await assetAdminUserClient.adminUserUpdate(params.id, params.usergroupid, params.userid, updateData);
      setUser({
        ...res.data,
        user_group: user.user_group,
      });
      setOffboardUserModalIsOpen(false);
      return true;
    } catch (err) {
      profileCardTranslations.saveErrorMessage =
        (axios.isAxiosError(err) && err?.response?.data?.message) || tUser('userPage.userSavingFailedErrorMessage');
      return false;
    }
  };

  const handleOnLinkClick = () => {
    if (location.state?.clickedFromPathname) {
      history.push(location.state.clickedFromPathname);
    } else {
      history.push(withCustomerOrgFormPath(routes.users, params.id));
    }
  };

  const handleInitiateOrderOnBehalfFlow = async () => {
    setLoading(true);
    try {
      // Get data needed when ordering on behalf
      const organization = (await assetAdminOrganizationClient.adminOrganizationRead(params.id)).data;
      const salesChannels = (await productServiceAdminSalesChannelsClient.getSalesChannels(organization.locale.country)).data.sales_channels;
      const salesChannel = salesChannels.find(sc => sc.uuid && sc.uuid === organization.sales_channel);

      if (!salesChannel) {
        throw new Error('Organization sales channel not found');
      }

      dispatch(setOrderUser(user));
      dispatch(setOrderOrganization(organization));
      dispatch(setOrderSalesChannel(salesChannel));

      setStorageItems(['tSelectedOrganization', 'tSalesChannel', 'tOOB'], [organization, salesChannel, user], 'session');

      history.push('/order');
    } catch {
      setInitiateOrderOnBehalfError(true);
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (user) {
      try {
        await assetAdminUserClient.adminUserDelete(organizationId, user.user_group!.id, user.id);
        handleOnLinkClick();
      } catch (err) {
        setDeleteUserError(
          axios.isAxiosError(err) && err.response?.data?.message
            ? `${tUser('userPage.deleteUserApiErrorMessagePrefix')} ${err.response.data.message}`
            : tUser('userPage.deleteUserGenericApiErrorMessage'),
        );
        setDeleteUserModalIsOpen(false);
      }
    }
  };

  const isOffboardingEnabled = offboardingFeatureNoEnabled && !user?.offboarding_at;
  const cancelOffboarding = () => setOffboardUserModalIsOpen(false);

  const render = () => {
    const deleteUserModal = (
      <ModalDialog
        destructive
        isOpen={deleteUserModalIsOpen}
        title={tUser('userPage.deleteUserModal.title')}
        acceptButtonText={tUser('userPage.deleteUserModal.deleteButton')}
        closeButtonText={tUser('userPage.deleteUserModal.cancelButton')}
        onCloseClick={() => setDeleteUserModalIsOpen(false)}
        onAcceptClick={handleDeleteUser}
      >
        <BodyText>{tUser('userPage.deleteUserModal.confirmationText')}</BodyText>
        <Gutter />
        <BodyText>
          {tUser('userPage.deleteUserModal.name', {
            name: user.name ?? tUser('userPage.deleteUserModal.inactiveUser'),
            interpolation: { escapeValue: false },
          })}
        </BodyText>
        <BodyText>{tUser('userPage.deleteUserModal.email', { email: user.email })}</BodyText>
        <BodyText>{tUser('userPage.deleteUserModal.phoneNumber', { phoneNumber: user.phone_number })}</BodyText>
      </ModalDialog>
    );

    const linkText = location.state?.clickedFromPage === 'usergroups' ? tUser('userPage.backToGroupLink') : tUser('userPage.backToUsersLink');
    const topContent = (
      <Div padding={{ vertical: 'md' }}>
        <PageHeader linkText={linkText} onLinkClick={handleOnLinkClick}>
          {user?.name ? user.name : tUser('userPage.ProfileCard.inactiveUser')}
        </PageHeader>
        {(user?.given_name == null || user?.family_name == null || user?.phone_number_verified === false) && <Gutter />}
        <Flex>
          {(user?.given_name == null || user?.family_name == null) && (
            <>
              <Tag backgroundColor="red600">{tUser('userPage.notOnboarded')}</Tag>
              <Gutter size="xs" />
            </>
          )}
          {user?.phone_number_verified === false && <Tag backgroundColor="red600">{tUser('userPage.phoneNumberNotVerified')}</Tag>}
        </Flex>
      </Div>
    );

    let leftSideContent;
    if (fetchUserError) {
      leftSideContent = <Message text={tUser('userPage.userFetchFailedErrorMessage')} />;
    } else if (loading) {
      leftSideContent = <Loader />;
    } else {
      leftSideContent = (
        <>
          <ProfileCard
            user={user}
            translations={profileCardTranslations}
            submitValues={handleUserSave}
            countryCallingCodes={COUNTRIES}
            isAdmin={true}
            isEmailEditable={emailUpdateEnabled}
          />
          <Gutter />
          <InvoicingAccounts userOrUserGroup={user} invoicingAccounts={organizationInvoicingAccounts} updateInvoicingAccount={handleUserSave} />
          <Gutter />
          <UserRightsAndLimitsCard
            user={user}
            // ignored since UserRole type is different in reusable-component-library due to different api generation tool
            // Reusable-component-library: UserRole: 'user-lite' | 'user' | 'approver' | 'orderer' | 'admin'
            // Here: UserRole: enum UserRole { UserLite = 'user-lite', User = 'user',...
            submitValues={handleUserSave}
            translations={userRightsAndLimitsTranslations}
            minBedriftLink={process.env.REACT_APP_MIN_BEDRIFT_URI || ''}
          />
          <Gutter />
          <AssetsCard
            assets={assets}
            getAssetLink={assetId => `/customer/${organizationId}/devices/${assetId}`}
            translations={assetsCardTranslations}
          />
          <Gutter size="xlg" />
          <Flex>
            {isOffboardingEnabled && (
              <Button variant="secondary" margin={{ left: 'sm' }} onClick={() => setOffboardUserModalIsOpen(true)}>
                {tUser('userPage.offboardUserButton')}
              </Button>
            )}
            <Button variant="destructive" margin={{ left: 'sm' }} onClick={() => setDeleteUserModalIsOpen(true)}>
              {tUser('userPage.deleteUserButton')}
            </Button>
          </Flex>
          <Gutter size="xlg" />
          {offboardUserModalIsOpen && (
            <OffboardUser
              translations={offboardUserTranslations}
              locale={organization?.locale.language ?? 'en'}
              user={user}
              assets={assets}
              submitValues={handleUserSave}
              onCancel={cancelOffboarding}
            />
          )}
        </>
      );
    }

    const rightSideContent = (
      <Flex directionColumn occupyVerticalSpace={false}>
        {user.policies.single_device_policy === UserPoliciesResponseSingleDevicePolicyEnum.InEffect && (
          <Flex spaceBetween>
            <Flex directionColumn>
              <Tag backgroundColor="orange400">{tUser('userPage.orderOnBehalfDeviceLimitFull')}</Tag>
              <Gutter />
            </Flex>
          </Flex>
        )}
        <Title size={'sm'}>{tUser('userPage.orderOnBehalfTitle')}</Title>
        <Gutter />
        <BodyText>{tUser('userPage.orderOnBehalfInfo')}</BodyText>
        <Gutter />
        <Flex>
          <Button
            variant="secondary"
            disabled={user.policies.single_device_policy === UserPoliciesResponseSingleDevicePolicyEnum.InEffect}
            onClick={handleInitiateOrderOnBehalfFlow}
          >
            {tUser('userPage.initiateOrderOnBehalfFlow')}
          </Button>
        </Flex>
        {initiateOrderOnBehalfError && (
          <GrowlNotification
            notification={tUser('userPage.initiateOrderOnBehalfFlowError')}
            variant={'error'}
            closeOnClick={() => setInitiateOrderOnBehalfError(false)}
          />
        )}
        <Gutter size="xlg" />
      </Flex>
    );

    return (
      <>
        {!loading && !fetchUserError && topContent}
        <DetailsPageMainContent>
          <DetailsPageMainContentLeftSide>{leftSideContent}</DetailsPageMainContentLeftSide>
          {!loading && !fetchUserError && <DetailsPageMainContentRightSide>{rightSideContent}</DetailsPageMainContentRightSide>}
        </DetailsPageMainContent>
        {deleteUserModal}
        {deleteUserError && <GrowlNotification notification={deleteUserError} variant={'error'} closeOnClick={() => setDeleteUserError(null)} />}
      </>
    );
  };

  return render();
};

export default UserProfile;
